/**
 * Ashrama Vashita System Data (D40)
 * 4 ASHRAMA (Life stages) × 10 VASHITA (Influences) = 40 divisions
 * Shows maternal lineage influence through life stages
 */

import { JatakaResponse, GrahaPosition } from '../../../../../models/api';

export interface PlanetDisplay {
  name: string;
  short_name: string;
  longitude: number;
  is_retrograde: boolean;
  is_lagna?: boolean;
  is_hora?: boolean;
  is_chandra?: boolean;
}

/**
 * 4 ASHRAMA types - life stages
 */
export const ASHRAMA_TYPES = [
  'BRAHMACHARYA', 'GRIHASTHA', 'VANAPRASTHA', 'SANYASA'
] as const;

export type AshramaType = typeof ASHRAMA_TYPES[number];

/**
 * 10 VASHITA types - spheres of influence
 */
export const VASHITA_TYPES = [
  'JANA', 'JANMA', 'AYUS', 'CHITTA', 'PARISHKURA',
  'DHARMA', 'RIDDHI', 'PRANIDHANA', 'KARMA', 'ADHI_MUKTI'
] as const;

export type VashitaType = typeof VASHITA_TYPES[number];

/**
 * ASHRAMA descriptions
 */
export const ASHRAMA_DESCRIPTIONS: Record<AshramaType, {
  name: string;
  sanskrit: string;
  description: string;
  degrees: string;
}> = {
  BRAHMACHARYA: {
    name: 'Student',
    sanskrit: 'Brahmacharya',
    description: 'Learning, celibacy, preparation',
    degrees: '00°00\' - 07°30\''
  },
  GRIHASTHA: {
    name: 'Householder',
    sanskrit: 'Grihastha',
    description: 'Family life, social duties',
    degrees: '07°30\' - 15°00\''
  },
  VANAPRASTHA: {
    name: 'Retirement',
    sanskrit: 'Vanaprastha',
    description: 'Teaching, mentorship, withdrawal',
    degrees: '15°00\' - 22°30\''
  },
  SANYASA: {
    name: 'Renunciation',
    sanskrit: 'Sanyasa',
    description: 'Complete renunciation, spiritual freedom',
    degrees: '22°30\' - 30°00\''
  }
};

/**
 * VASHITA descriptions
 */
export const VASHITA_DESCRIPTIONS: Record<VashitaType, {
  name: string;
  sanskrit: string;
  sphere: string;
}> = {
  JANA: {
    name: 'Origin',
    sanskrit: 'Jana',
    sphere: 'Birth place, social origin'
  },
  JANMA: {
    name: 'Birth',
    sanskrit: 'Janma',
    sphere: 'Birth circumstances'
  },
  AYUS: {
    name: 'Longevity',
    sanskrit: 'Ayus',
    sphere: 'Life span, vitality'
  },
  CHITTA: {
    name: 'Consciousness',
    sanskrit: 'Chitta',
    sphere: 'Mind, mental patterns'
  },
  PARISHKURA: {
    name: 'Creativity',
    sanskrit: 'Parishkura',
    sphere: 'Creative radiance'
  },
  DHARMA: {
    name: 'Purpose',
    sanskrit: 'Dharma',
    sphere: 'Duty, life path'
  },
  RIDDHI: {
    name: 'Prosperity',
    sanskrit: 'Riddhi',
    sphere: 'Wealth, success'
  },
  PRANIDHANA: {
    name: 'Overcoming',
    sanskrit: 'Pranidhana',
    sphere: 'Obstacles overcome'
  },
  KARMA: {
    name: 'Activity',
    sanskrit: 'Karma',
    sphere: 'Actions, work'
  },
  ADHI_MUKTI: {
    name: 'Confidence',
    sanskrit: 'Adhi Mukti',
    sphere: 'Freedom from anxiety'
  }
};

/**
 * Ashrama Vashita division attributes
 */
export interface AshramaVashitaAttributes {
  planet: PlanetDisplay;
  positionInRashi: number; // 0°-30° position within rashi
  ashramaNumber: number; // 1-4 (which ASHRAMA)
  ashramaType: AshramaType;
  vashitaNumber: number; // 1-10 (which VASHITA)
  vashitaType: VashitaType;
  amshaNumber: number; // 1-40 (final division number)
  interpretation: string;
}

/**
 * Complete Ashrama Vashita data structure
 */
export interface AshramaVashitaData {
  divisions: AshramaVashitaAttributes[];
}

/**
 * Parse longitude from position string
 */
export function parseLongitudeFromPosition(position: string): number {
  if (!position) {
    return 0;
  }

  const parts = position.split('|');
  if (parts.length >= 3) {
    return parseFloat(parts[2]);
  }

  return 0;
}

/**
 * Calculate ASHRAMA number from degrees in rashi
 * Formula: floor(degrees ÷ 7.5) + 1
 */
export function calculateAshramaNumber(degreeInRashi: number): number {
  const ashramaIndex = Math.floor(degreeInRashi / 7.5);
  return Math.min(ashramaIndex + 1, 4); // Cap at 4
}

/**
 * Calculate VASHITA number from degrees in rashi
 * Formula: floor((degrees mod 7.5) ÷ 0.75) + 1
 */
export function calculateVashitaNumber(degreeInRashi: number): number {
  const remainderInAshrama = degreeInRashi % 7.5;
  const vashitaIndex = Math.floor(remainderInAshrama / 0.75);
  return Math.min(vashitaIndex + 1, 10); // Cap at 10
}

/**
 * Calculate Ashrama Vashita amsha number (1-40)
 * Formula: (ASHRAMA_number - 1) × 10 + VASHITA_number
 */
export function calculateAmshaNumber(ashramaNumber: number, vashitaNumber: number): number {
  return (ashramaNumber - 1) * 10 + vashitaNumber;
}

/**
 * Get ASHRAMA type from number
 */
export function getAshramaType(ashramaNumber: number): AshramaType {
  return ASHRAMA_TYPES[ashramaNumber - 1] || ASHRAMA_TYPES[0];
}

/**
 * Get VASHITA type from number
 */
export function getVashitaType(vashitaNumber: number): VashitaType {
  return VASHITA_TYPES[vashitaNumber - 1] || VASHITA_TYPES[0];
}

/**
 * Generate interpretation based on ASHRAMA and VASHITA combination
 */
export function getAshramaVashitaInterpretation(ashramaType: AshramaType, vashitaType: VashitaType): string {
  const ashrama = ASHRAMA_DESCRIPTIONS[ashramaType];
  const vashita = VASHITA_DESCRIPTIONS[vashitaType];

  return `${vashita.sphere} in ${ashrama.sanskrit.toLowerCase()} stage`;
}

/**
 * Convert API planet to PlanetDisplay format
 */
export function convertToPlanetDisplay(planet: GrahaPosition, currentHoraPlanet?: string): PlanetDisplay {
  const longitude = parseLongitudeFromPosition(planet.position || '');

  const isHora = currentHoraPlanet && (
    planet.short_name === currentHoraPlanet ||
    planet.name === currentHoraPlanet
  );

  return {
    name: planet.name,
    short_name: planet.short_name,
    longitude: longitude,
    is_retrograde: planet.is_retrograde || false,
    is_lagna: planet.short_name === 'Lg',
    is_hora: isHora || false,
    is_chandra: planet.short_name === 'Ch' || planet.name === 'Chandra'
  };
}

/**
 * Calculate Ashrama Vashita division for a planet
 */
export function calculatePlanetAshramaVashita(planet: PlanetDisplay): AshramaVashitaAttributes {
  // Position within current rashi (0°-30°)
  const positionInRashi = planet.longitude % 30;

  // Calculate ASHRAMA and VASHITA
  const ashramaNumber = calculateAshramaNumber(positionInRashi);
  const vashitaNumber = calculateVashitaNumber(positionInRashi);

  // Get types
  const ashramaType = getAshramaType(ashramaNumber);
  const vashitaType = getVashitaType(vashitaNumber);

  // Calculate final amsha number (1-40)
  const amshaNumber = calculateAmshaNumber(ashramaNumber, vashitaNumber);

  // Generate interpretation
  const interpretation = getAshramaVashitaInterpretation(ashramaType, vashitaType);

  return {
    planet,
    positionInRashi,
    ashramaNumber,
    ashramaType,
    vashitaNumber,
    vashitaType,
    amshaNumber,
    interpretation
  };
}

/**
 * Calculate all Ashrama Vashita divisions from API response
 */
export function calculateAshramaVashitas(apiResponse: JatakaResponse): AshramaVashitaData {
  if (!apiResponse?.graha_positions?.planets) {
    return { divisions: [] };
  }

  // Get current hora planet for highlighting
  const currentHoraPlanet = apiResponse.detailed_panchanga?.hora_details?.current_planet;
  const horaPlanetName = currentHoraPlanet ? currentHoraPlanet.split(' ')[0] : undefined;

  // Convert API planets to PlanetDisplay format, INCLUDING Lagna, Rahu, Ketu
  const planets = apiResponse.graha_positions.planets
    .map(planet => convertToPlanetDisplay(planet, horaPlanetName));

  // Calculate Ashrama Vashita for each planet
  const divisions = planets.map(planet => calculatePlanetAshramaVashita(planet));

  // Sort by amsha number for display
  divisions.sort((a, b) => a.amshaNumber - b.amshaNumber);

  return { divisions };
}
